import { ComponentIds, HierarchyUtils } from '@inter-ikea-core/core';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import {
  KompisSheetBody,
  KompisSheetBodyPadding,
  KompisSheetFooter,
  KompisSheetFooterButtons,
  KompisSheetHeader
} from '@inter-ikea-kompis/react-components';
import { useCore } from 'contexts/core';
import { useAppDispatch } from 'ducks/hooks';

import { getMarketProducts } from 'ducks/products';
import {
  getFrontStyleSheetEntity,
  isFrontStyleSheetOpen,
  setFrontStyleSheetNotVisible
} from 'ducks/sheets';
import {
  addFrontStyleToFrame,
  getFrontsInScene,
  getMajorityProduct
} from 'rangeConfig/shared/utils/frontLayouts';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Sheet from 'ui/Sheet/Sheet';
import Body from 'ui/Typography/Body';
import Title from 'ui/Typography/Title';
import media from 'ui/media';
import { getRange } from 'utils/queryUtils';
import { alignedFramesHasSlidingDoor } from '@inter-ikea-storageone/range-platsa';
import { ProductType, SlidingDoorComponent } from '@inter-ikea-storageone/range';
import { showDoors } from 'ducks/layout';
import { autoFillDoorsAtTarget } from 'rangeConfig/pax/utils/autoFillDoors';
import { SkapaTheme } from '@inter-ikea-kompis/themes';

const TitleWrapper = styled.div`
  ${media.mobile`
    width: 100%;
    padding: 0.5rem 0;
  `}
`;

const FrontStyleSheet = () => {
  const dispatch = useAppDispatch();
  const range = getRange();
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const products = useSelector(getMarketProducts);

  const visible = useSelector(isFrontStyleSheetOpen);

  const frameToAdd = useSelector(getFrontStyleSheetEntity);

  if (!frameToAdd) {
    return null;
  }

  const doorsInScene =
    range === 'pax'
      ? coreApi.entities.getByComponents([ComponentIds.DOOR_COMPONENT_ID, SlidingDoorComponent.id])
      : getFrontsInScene(coreApi);
  const majorityDoorId = getMajorityProduct(doorsInScene);
  const frontsOrDoorProduct = products.find(p => p.coreProductId === majorityDoorId);

  const handlesInScene = coreApi.entities.getByComponent(ComponentIds.HANDLE_COMPONENT_ID);
  const majorityHandleId = getMajorityProduct(handlesInScene);

  const alignedFramesHaveSlidingDoors = alignedFramesHasSlidingDoor(frameToAdd, coreApi);

  const frontProductIsMostlySlidingDoors =
    frontsOrDoorProduct?.type === ProductType.PLATSA_SLIDING_DOOR_ASM;

  const onClose = () => {
    dispatch(setFrontStyleSheetNotVisible(false));
  };

  const addFrontStyle = () => {
    if (range === 'pax' && frontsOrDoorProduct) {
      autoFillDoorsAtTarget(
        frontsOrDoorProduct.coreProductId,
        ['S40301124_ASM', 'S10301125_ASM', 'S20414829_ASM', 'S00414830_ASM'],
        coreApi,
        frameToAdd,
        products
      );
    }

    if (frontsOrDoorProduct && frameToAdd) {
      if (alignedFramesHaveSlidingDoors && frontProductIsMostlySlidingDoors) {
        coreApi.products.addToTarget(frontsOrDoorProduct.coreProductId, frameToAdd);
      } else addFrontStyleToFrame(frontsOrDoorProduct, frameToAdd, products, coreApi);
    }

    if (majorityHandleId) {
      // Add handles
      // First get the newly added frames doors
      const doorsOnNewFrame = HierarchyUtils.hierarchy(frameToAdd).filter(
        c =>
          c.hasComponent(ComponentIds.DOOR_COMPONENT_ID) ||
          c.hasComponent(SlidingDoorComponent.id) ||
          c.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)
      );

      // If we find doors, add handle to all doors
      if (doorsOnNewFrame) {
        doorsOnNewFrame.forEach(door => coreApi.products.addToTarget(majorityHandleId, door));
      }
    }
  };

  return (
    <Sheet data-testid="front-styles-sheet" visible={visible} onOverlayClick={onClose}>
      <KompisSheetHeader theme={SkapaTheme} onCloseButtonClick={onClose} heading="" />
      <KompisSheetBody theme={SkapaTheme}>
        <KompisSheetBodyPadding theme={SkapaTheme}>
          <TitleWrapper>
            <Title pb="s" type={ThemeFontStyleTypeEnum.headingMedium}>
              {formatMessage({ id: 'storageone.frontStylesSheetHeading' })}
            </Title>
          </TitleWrapper>

          <Body py="md" mb="xxxl" type={ThemeFontStyleTypeEnum.bodyMedium}>
            {formatMessage({ id: 'storageone.frontStylesSheetDescription' })}
          </Body>
        </KompisSheetBodyPadding>
      </KompisSheetBody>
      <KompisSheetFooter theme={SkapaTheme}>
        <KompisSheetFooterButtons
          compact={false}
          data-testid="front-style-sheet-footer-buttons"
          theme={SkapaTheme}
          primaryLabel={formatMessage({ id: 'storageone.frontStylesSheetButtonYes' })}
          secondaryLabel={formatMessage({ id: 'storageone.frontStylesSheetButtonNo' })}
          onButtonClick={event => {
            if (event.detail.type === 'primaryButton') {
              addFrontStyle();
              dispatch(showDoors());
              dispatch(setFrontStyleSheetNotVisible(true));
            } else {
              dispatch(setFrontStyleSheetNotVisible(false));
            }
          }}
        />
      </KompisSheetFooter>
    </Sheet>
  );
};

export default FrontStyleSheet;
